import { Validators } from '../../../../../utils/validators';
import { FormModel } from '../../../../../components/Form';
import { bookingConfirmationBy1 } from '../../../../../utils/constants/data';
import { isCustomizePackage } from 'utils/pricePackage';

type editBookingProps = {
  strings: any;
  pricePackages: { value: any; label: string }[];
  venues: { value: any; label: string }[];
  eventTypes: { value: any; label: string }[];
  layouts: { value: any; label: string }[];
  partOfDays: { value: any; label: string }[];
  serviceChargeDropdown: any;
  serviceCharges: any;
  hourlyPricing: boolean;
  isOdc: boolean;
  eventSubtypeOptions?: any[];
  excessGuestsPercentage?: number | null;
  selectedPricePackage?: any;
  taxGroups?: { value: any; label: string }[];
  layoutMaxCapacity?: number | null;
  layoutName?: string | null;
  layoutSelected?: boolean;
  venueMaxCapacity?: number | null;
  venueName?: string | null;
};

/**
 * Part 1: Event Details + Schedule + Pricing Configuration header.
 * PricingStrategySection is rendered next in the page JSX under that header.
 */
export const EditBookingForm = ({
  strings,
  pricePackages,
  venues,
  eventTypes,
  layouts,
  partOfDays,
  serviceChargeDropdown,
  serviceCharges,
  hourlyPricing,
  eventSubtypeOptions,
  excessGuestsPercentage,
  selectedPricePackage,
  layoutMaxCapacity,
  layoutName,
  layoutSelected = false,
  venueMaxCapacity,
  venueName,
}: editBookingProps): FormModel[] => {
  const showCustomPackageName = !hourlyPricing && isCustomizePackage(selectedPricePackage, pricePackages);
  let formFields: FormModel[] = [
    {
      label: strings.new_booking_section_event_details,
      value: '',
      type: 'section',
      placeholder: '',
      field: 'event_config_section',
      autoFocus: true,
      validators: [],
      required: false,
      responsive: { xs: 12, sm: 12 },
      compactSection: true,
    },
    {
      label: strings.new_booking_venue,
      value: '',
      size: 'medium',
      type: 'drop-down',
      placeholder: '',
      field: 'venue',
      validators: [{ check: Validators.validateMandatoryField }],
      required: true,
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      options: venues,
      disabled: true,
    },
    {
      label: strings.new_booking_segment,
      value: '',
      size: 'medium',
      type: 'drop-down',
      placeholder: '',
      field: 'eventType',
      validators: [{ check: Validators.validateMandatoryField }],
      required: true,
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      options: eventTypes,
      disabled: true,
    },
    ...(eventSubtypeOptions && eventSubtypeOptions.length > 0
      ? [
          {
            label: strings.new_booking_event_sub_type,
            value: {},
            size: 'medium',
            type: 'drop-down',
            placeholder: strings.new_booking_event_sub_type,
            field: 'eventSubtype',
            validators: [{ check: Validators.validateMandatoryField }],
            responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
            required: true,
            options: eventSubtypeOptions || [],
          } as FormModel,
        ]
      : [
          {
            label: strings.new_booking_event_sub_type,
            value: '',
            size: 'medium',
            autoFocus: false,
            type: 'text',
            typeValue: 'text',
            variant: 'outlined',
            placeholder: '',
            field: 'eventSubtype',
            validators: [{ check: Validators.validateString }],
            responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
            required: false,
          } as FormModel,
        ]),
    {
      label: strings.new_booking_laouts,
      value: {},
      size: 'medium',
      type: 'drop-down',
      placeholder: strings.new_booking_laouts,
      field: 'layout',
      validators: [],
      required: false,
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      options: layouts,
      isMulti: false,
    },
    {
      label: strings.new_booking_number_of_guests,
      value: '',
      type: 'guest-count-split',
      placeholder: '',
      field: 'guaranteedGuests',
      validators: [],
      required: true,
      responsive: { xs: 12, sm: 6 },
      excessGuestsPercentage,
      venueMaxCapacity,
      venueName,
      layoutMaxCapacity,
      layoutName,
      layoutSelected,
      expectedGuestsLabel: strings.venueDetailsTabsExpected,
    },
    {
      label: strings.venue_text_excessGuestPercent,
      value: '',
      size: 'medium',
      autoFocus: false,
      type: 'text',
      typeValue: 'number',
      variant: 'outlined',
      placeholder: '',
      field: 'excessGuestsPercentage',
      validators: [{ check: Validators.validateFloat, max: 100 }],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: false,
      endAdornment: '%',
    },
    {
      label: strings.new_booking_service_charge,
      value: '',
      typeValue: 'number',
      type: 'text',
      placeholder: '',
      field: 'serviceChargePercentage',
      validators: [{ check: Validators.validateFloat, max: 100 }],
      required: false,
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      endAdornment: '%',
    },

    {
      label: strings.new_booking_section_schedule,
      value: '',
      type: 'section',
      placeholder: '',
      field: 'schedule_config_section',
      autoFocus: true,
      validators: [],
      required: false,
      responsive: { xs: 12, sm: 12 },
      compactSection: true,
    },
    {
      label: strings.new_booking_start_date,
      value: '',
      size: 'medium',
      autoFocus: false,
      type: 'text',
      typeValue: 'text',
      variant: 'outlined',
      placeholder: '',
      field: 'startDate',
      validators: [],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: true,
      disabled: true,
    },
    {
      label: strings.new_booking_end_date,
      value: '',
      size: 'medium',
      autoFocus: false,
      type: 'text',
      typeValue: 'text',
      variant: 'outlined',
      placeholder: '',
      field: 'endDate',
      validators: [],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: true,
      disabled: true,
    },
    {
      label: `${strings.timeSlot_column_startTime}`,
      value: '',
      size: 'medium',
      type: 'time',
      typeValue: 'text',
      variant: 'outlined',
      placeholder: '',
      field: 'startTime',
      validators: [],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: true,
    },
    {
      label: `${strings.timeSlot_text_endTime}`,
      value: '',
      size: 'medium',
      type: 'time',
      typeValue: 'text',
      variant: 'outlined',
      placeholder: '',
      field: 'endTime',
      validators: [],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: true,
    },
    {
      label: strings.venueConfig_general_preLeadTime,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'number',
      variant: 'outlined',
      placeholder: '',
      field: 'preLeadTime',
      validators: [{ check: Validators.validateMandatoryField }, { check: Validators.validateInt, max: 1440 }],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: true,
      disabled: false,
      endAdornment: 'min',
    },
    {
      label: strings.venueConfig_general_postLeadTime,
      value: '',
      size: 'medium',
      type: 'text',
      typeValue: 'number',
      variant: 'outlined',
      placeholder: '',
      field: 'postLeadTime',
      validators: [{ check: Validators.validateMandatoryField }, { check: Validators.validateInt, max: 1440 }],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: true,
      disabled: false,
      endAdornment: 'min',
    },
    {
      label: strings.timeSlot_column_partOfDay,
      value: '',
      size: 'medium',
      type: 'drop-down',
      typeValue: 'text',
      variant: 'outlined',
      placeholder: '',
      field: 'partOfDay',
      validators: [{ check: Validators.validateMandatoryField }],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: true,
      options: partOfDays,
    },
    ...(hourlyPricing
      ? []
      : [
          {
            label: strings.new_booking_package,
            value: {},
            size: 'medium',
            type: 'drop-down',
            placeholder: '',
            field: 'pricePackage',
            validators: [{ check: Validators.validateMandatoryField }],
            required: true,
            responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
            options: pricePackages,
          } as FormModel,
        ]),
    ...(showCustomPackageName
      ? [
          {
            label: strings.new_booking_custom_package_name,
            value: '',
            size: 'medium',
            autoFocus: false,
            type: 'text',
            typeValue: 'text',
            variant: 'outlined',
            placeholder: '',
            field: 'customPricePackageName',
            validators: [{ check: Validators.validateString }],
            responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
            required: false,
          } as FormModel,
        ]
      : []),

    {
      label: 'Pricing Configuration',
      value: '',
      type: 'section',
      placeholder: '',
      field: 'financial_config_section',
      autoFocus: true,
      validators: [],
      required: false,
      responsive: { xs: 12, sm: 12 },
      compactSection: true,
    },
  ];

  if (serviceChargeDropdown) {
    const idx = formFields.findIndex((i: any) => i.field === 'serviceChargePercentage');
    formFields[idx] = {
      label: strings.new_booking_service_charge,
      value: {},
      size: 'medium',
      type: 'drop-down',
      placeholder: `${strings.new_booking_service_charge} `,
      field: 'serviceCharge',
      validators: [],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      required: false,
      options: serviceCharges,
    };
  }
  return formFields;
};

/**
 * Part 2: Taxes + Event Notes (rendered after PricingStrategySection).
 */
export const EditBookingTaxForm = ({
  strings,
  taxGroups = [],
}: {
  strings: any;
  taxGroups?: { value: any; label: string }[];
}): FormModel[] => [
  {
    label: 'Taxes',
    value: '',
    type: 'section',
    placeholder: '',
    field: 'tax_config_section',
    autoFocus: true,
    validators: [],
    required: false,
    responsive: { xs: 12, sm: 12 },
    compactSection: true,
  },
  {
    label: strings.venue_text_taxGroupOnGuestCharge,
    value: {},
    size: 'medium',
    type: 'drop-down',
    placeholder: strings.venue_text_taxGroupOnGuestCharge,
    field: 'taxGroupOnGuestCharge',
    validators: [],
    responsive: { xs: 12, sm: 6 },
    required: false,
    isMulti: false,
    disabled: false,
    options: taxGroups,
  },
  {
    label: strings.venue_text_taxGroupOnHallRental,
    value: {},
    size: 'medium',
    type: 'drop-down',
    placeholder: strings.venue_text_taxGroupOnHallRental,
    field: 'taxGroupOnHallRental',
    validators: [],
    responsive: { xs: 12, sm: 6 },
    required: false,
    isMulti: false,
    disabled: false,
    options: taxGroups,
  },
  {
    label: strings.new_booking_customer_additionalComments,
    value: '',
    type: 'section',
    placeholder: '',
    field: 'event_notes_section',
    autoFocus: true,
    validators: [],
    required: false,
    responsive: { xs: 12, sm: 12 },
    compactSection: true,
  },
  {
    label: '',
    value: '',
    type: 'textEditor',
    placeholder: strings.new_booking_event_notes_placeholder,
    field: 'additionalComments',
    validators: [],
    responsive: { xs: 12 },
    required: false,
  },
];

/**
 * Part 3: Remaining fields under Others (after Add-ons).
 */
export const EditBookingOthersForm = ({
  strings,
  isOdc,
  holdReleaseBaseDate = null,
}: {
  strings: any;
  isOdc: boolean;
  holdReleaseBaseDate?: string | Date | null;
}): FormModel[] => {
  const bookingConfirmationOptions = bookingConfirmationBy1(strings);
  return [
    {
      label: strings.edit_booking_others_text,
      value: '',
      type: 'section',
      placeholder: '',
      field: 'others_section',
      autoFocus: true,
      validators: [],
      required: false,
      responsive: { xs: 12, sm: 12 },
      compactSection: true,
    },
    {
      label: strings.venueConfig_payment_bookingConfirmation,
      value: '',
      size: 'medium',
      type: 'drop-down',
      placeholder: '',
      field: 'bookingConfirmationBy',
      validators: [{ check: Validators.validateMandatoryField }],
      responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
      options: bookingConfirmationOptions,
      required: true,
    },
    {
      label: strings.edit_booking_hold_days,
      value: '',
      size: 'medium',
      autoFocus: false,
      type: 'text',
      typeValue: 'number',
      variant: 'outlined',
      placeholder: '',
      field: 'holdDays',
      validators: [{ check: Validators.validateInt, message: strings.invalidHoldDaysMsg, max: 1800 }],
      // Wider when release hint is shown so warning text fits beside the field.
      responsive: holdReleaseBaseDate
        ? { xs: 12, sm: 12, md: 8, lg: 6 }
        : { xs: 12, sm: 6, md: 4, lg: 3 },
      required: false,
      inputProps: { maxLength: 4 },
      holdReleaseBaseDate,
    },
    ...(isOdc
      ? [
          {
            label: strings.customerDetailsPlaceOfEvent,
            value: '',
            size: 'medium',
            autoFocus: false,
            type: 'text',
            typeValue: 'text',
            variant: 'outlined',
            placeholder: '',
            field: 'placeOfEvent',
            validators: [{ check: Validators.validateString }],
            responsive: { xs: 12, sm: 6, md: 4, lg: 3 },
            required: false,
          } as FormModel,
          {
            label: strings.customerDetailsAddress,
            value: '',
            size: 'medium',
            autoFocus: false,
            type: 'text',
            typeValue: 'text',
            variant: 'outlined',
            placeholder: '',
            field: 'addressOfEvent',
            validators: [{ check: Validators.validateString }],
            responsive: { xs: 12, sm: 6 },
            required: false,
          } as FormModel,
        ]
      : []),
  ];
};

/** All form models combined — used for error-field label lookup on validation failure. */
export const getAllEditBookingFormModels = (props: editBookingProps): FormModel[] => [
  ...EditBookingForm(props),
  ...EditBookingTaxForm({ strings: props.strings, taxGroups: props.taxGroups }),
  ...EditBookingOthersForm({ strings: props.strings, isOdc: props.isOdc }),
];
